import { useGetAccountGlobalFormFieldsQuery } from "api/User/hooks.ts";
import { BasicRuntimeFormField } from "components/Forms/BasicRuntimeFormField/BasicRuntimeFormField.tsx";
import { FormCol } from "components/UI/Form/FormCol";
import { FormGrid } from "components/UI/Form/FormGrid";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useMemo } from "react";

export type AccountGlobalFieldSection = "personal" | "contact" | "company";

export const accountGlobalFieldRows: Record<AccountGlobalFieldSection, string[][]> = {
    personal: [
        ["name_first", "name_last"],
        ["phone_prefix", "phone"],
        ["domains"],
        ["address"],
        ["address_street", "address_street_number"],
        ["address_city", "address_zip"],
        ["address_state", "address_country"],
    ],
    contact: [
        ["address_contact_status"],
        ["address_contact"],
        ["address_contact_street", "address_contact_street_number"],
        ["address_contact_city", "address_contact_zip"],
        ["address_contact_state", "address_contact_country"],
    ],
    company: [
        ["company_name"],
        ["company_registration", "company_vat"],
        ["company_address"],
        ["company_street", "company_street_number"],
        ["company_city", "company_zip"],
        ["company_state", "company_country"],
        ["company_invoicing_email"],
    ],
};

export const AccountGlobalFieldsSection: FC<{ section: AccountGlobalFieldSection }> = ({ section }) => {
    const { t } = usePaldockTranslation("forms.account-profile");
    const accountGlobalFieldsQuery = useGetAccountGlobalFormFieldsQuery();
    const fieldsBySystemName = useMemo(
        () =>
            new Map(
                accountGlobalFieldsQuery.data?.globalFormFields.map((item) => [item.field.system_name, item.field])
            ),
        [accountGlobalFieldsQuery.data?.globalFormFields]
    );

    return (
        <FormCol>
            {accountGlobalFieldRows[section].map((row) => (
                <FormGrid key={row.join("-")} cols={1} mdCols={row.length === 1 ? 1 : 2}>
                    {row.map((systemName) => {
                        const field = fieldsBySystemName.get(systemName);

                        return field ? (
                            <BasicRuntimeFormField
                                key={field.id}
                                field={field}
                                name={`global_form_field_values.${systemName}`}
                                label={t(`global_fields.${systemName}`)}
                                countrySource="central"
                                countryValue="iso2"
                            />
                        ) : null;
                    })}
                </FormGrid>
            ))}
        </FormCol>
    );
};
